<template>
  <div id="app">
    <!-- Header -->
    <div class="header-toolbar">
      <div class="header-content">
        <div class="logo-section">
          <div class="logo-icon">
            <span class="icon-pulse">📱</span>
          </div>
          <h1 class="dashboard-title">SIM Control Dashboard</h1>
        </div>
        <div class="header-controls">
          <div class="live-indicator">
            <div :class="['live-dot', { 'loading': loading }]"></div>
            <span style="color: white; font-size: 0.875rem;">
              {{ loading ? 'Loading...' : 'Live' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- API Notification -->
    <div v-if="apiNotification" :class="[
      'api-notification',
      'notification-' + apiNotification.type
    ]">
      <div class="notification-content">
        <p class="notification-message">{{ apiNotification.message }}</p>
        <button @click="apiNotification = null" class="notification-close">×</button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content">
      <!-- Statistics Section -->
      <div class="stats-section">
        <div class="stats-header">
          <h3 class="stats-title">Statistics Overview</h3>
        </div>

        <div class="stats-wrapper">
          <div class="stats-container">
            <div class="stat-card">
              <div class="stat-label">Total SIMs</div>
              <div class="stat-value">{{ stats.total }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">✓</span>
                <span>{{ stats.active }} Active, {{ stats.suspended }} Suspended</span>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-label">Low Data SIMs</div>
              <div class="stat-value">{{ stats.criticalAlerts }}</div>
              <div class="stat-change negative">
                <span class="stat-icon">⚠</span>
                <span>{{ stats.criticalData }} Critical, {{ stats.warningAlerts }} Warning</span>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-label">Total Data Used (MB)</div>
              <div class="stat-value">{{ stats.totalDataUsedMb.toFixed(1) }}</div>
              <div class="stat-change positive">
                <span class="stat-icon">💧</span>
                <span>{{ stats.totalDataRemainingMb.toFixed(1) }} MB remaining</span>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-label">Data Usage Change</div>
              <div class="stat-value">{{ Math.abs(comparisonData.percentageUsed).toFixed(1) }}%</div>
              <div class="stat-change" :class="comparisonData.percentageUsed > 0 ? 'negative' : 'positive'">
                <span class="stat-icon">{{ comparisonData.percentageUsed > 0 ? '📉' : '📈' }}</span>
                <span>{{ comparisonData.dataUsed.toFixed(1) }} MB consumed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Comparison Section -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">Data Usage Comparison</div>
        </div>

        <div class="comparison-content">
          <div class="comparison-grid">
            <div class="comparison-card">
              <h4 class="comparison-title">Previous Data</h4>
              <div class="comparison-detail">
                <span class="detail-label">Total Bundle:</span>
                <span class="detail-value">{{ comparisonData.previousTotal.toFixed(1) }} MB</span>
              </div>
              <div class="comparison-detail">
                <span class="detail-label">Remaining:</span>
                <span class="detail-value primary">{{ comparisonData.previousRemaining.toFixed(1) }} MB</span>
              </div>
            </div>

            <div class="comparison-card">
              <h4 class="comparison-title">Current Data</h4>
              <div class="comparison-detail">
                <span class="detail-label">Total Bundle:</span>
                <span class="detail-value">{{ comparisonData.currentTotal.toFixed(1) }} MB</span>
              </div>
              <div class="comparison-detail">
                <span class="detail-label">Remaining:</span>
                <span class="detail-value primary">{{ comparisonData.currentRemaining.toFixed(1) }} MB</span>
              </div>
            </div>

            <div class="comparison-card highlight">
              <h4 class="comparison-title">Data Consumed</h4>
              <div class="comparison-detail">
                <span class="detail-label">Usage:</span>
                <span class="detail-value accent">{{ comparisonData.dataUsed.toFixed(1) }} MB</span>
              </div>
              <div class="comparison-detail">
                <span class="detail-label">Change:</span>
                <span class="detail-value accent">{{ Math.abs(comparisonData.percentageUsed).toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <div class="chart-card">
          <div class="chart-header">
            <h3 class="chart-title">Data Distribution</h3>
          </div>
          <div class="chart-body">
            <canvas ref="dataDistributionChart" width="300" height="300"></canvas>
          </div>
          <div class="chart-legend">
            <div class="legend-item">
              <div class="legend-dot critical"></div>
              <span>Critical (0-10MB)</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot warning"></div>
              <span>Low (10-50MB)</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot medium"></div>
              <span>Medium (50-100MB)</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot good"></div>
              <span>Good (>100MB)</span>
            </div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-header">
            <h3 class="chart-title">SIM Status</h3>
          </div>
          <div class="chart-body">
            <canvas ref="statusChart" width="300" height="300"></canvas>
          </div>
          <div class="chart-legend">
            <div class="legend-item">
              <div class="legend-dot active"></div>
              <span>{{ stats.active }} Active</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot suspended"></div>
              <span>{{ stats.suspended }} Suspended</span>
            </div>
            <div class="legend-item">
              <div class="legend-dot inactive"></div>
              <span>{{ stats.inactive }} Inactive</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Section -->
      <div class="search-section">
        <div class="filter-header">
          <h3 class="filter-title">Filters</h3>
        </div>

        <div class="search-controls-wrapper">
          <div class="search-controls">
            <div class="search-group">
              <label class="search-label">Search SIMs</label>
              <input type="text" class="search-input" v-model="searchQuery" placeholder="Search MSISDN, Network...">
            </div>

            <div class="search-group">
              <label class="search-label">Filter by Status</label>
              <select v-model="filterStatus" class="search-select">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div class="search-group">
              <label class="search-label">Filter by Alert Level</label>
              <select v-model="filterAlertLevel" class="search-select">
                <option value="">All Levels</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="ok">OK</option>
              </select>
            </div>

            <div class="search-group">
              <label class="search-label">Sort by</label>
              <select v-model="sortBy" class="search-select">
                <option value="msisdn">MSISDN</option>
                <option value="data-asc">Data Balance (Low to High)</option>
                <option value="data-desc">Data Balance (High to Low)</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          <div class="filter-actions">
            <button class="apply-filters-btn" @click="refreshData">
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      <!-- Data Grid -->
      <div class="grid-container">
        <div class="grid-header">
          <div class="grid-title">SIM Details</div>
          <div class="grid-controls">
            <div class="grid-actions">
              <button class="action-button refresh-btn" @click="refreshData" :disabled="loading" title="Refresh">
                <span class="btn-icon">🔄</span>
                <span>{{ loading ? 'Loading...' : 'Refresh' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="table-container">
          <div v-if="loading" class="loading-overlay">
            <div class="loader"></div>
          </div>

          <table v-if="!loading && sortedAndFilteredSims.length > 0" class="data-table">
            <thead>
              <tr>
                <th>MSISDN</th>
                <th>Status</th>
                <th>Data Balance</th>
                <th>Alert Level</th>
                <th>Network</th>
                <th>Usage (MB)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sim in sortedAndFilteredSims.slice(0, 20)" :key="sim.id" @click="goToSimControl(sim.msisdn)"
                style="cursor: pointer;">
                <td>
                  <span class="device-id">{{ sim.msisdn }}</span>
                </td>
                <td>
                  <span :class="[
                    'activity-badge',
                    'badge-' + sim.status
                  ]">
                    {{ sim.status }}
                  </span>
                </td>
                <td>
                  <div class="data-progress">
                    <div class="progress-bar-container">
                      <div class="progress-bar" :style="{ width: (sim.RemainingMb / 200 * 100) + '%' }" :class="[
                        sim.RemainingMb <= 10 ? 'critical' :
                          sim.RemainingMb <= 50 ? 'warning' :
                            sim.RemainingMb <= 100 ? 'medium' : 'good'
                      ]"></div>
                    </div>
                    <span class="progress-label">{{ sim.RemainingMb.toFixed(1) }} MB</span>
                  </div>
                </td>
                <td>
                  <span :class="[
                    'activity-badge',
                    'badge-' + sim.alert_level
                  ]">
                    {{ sim.alert_level }}
                  </span>
                </td>
                <td>{{ sim.network }}</td>
                <td>
                  <span class="volume-display">{{ sim.DataUsedMb.toFixed(1) }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && sortedAndFilteredSims.length === 0" class="empty-state">
            <div class="empty-icon">📂</div>
            <div class="empty-title">No SIMs Found</div>
            <div class="empty-description">
              Try adjusting your filters or refresh the data
            </div>
          </div>
        </div>

        <div class="pagination-container">
          <div class="pagination-info">
            Showing 1-{{ Math.min(20, sortedAndFilteredSims.length) }} of {{ sortedAndFilteredSims.length }} SIMs
            <span class="per-page-info">(Total: {{ sims.length }})</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SimControlMainDashboard',
  data() {
    return {
      sims: [],
      stats: {
        total: 0,
        active: 0,
        inactive: 0,
        suspended: 0,
        criticalAlerts: 0,
        warningAlerts: 0,
        okCount: 0,
        criticalData: 0,
        lowData: 0,
        mediumData: 0,
        goodData: 0,
        totalDataUsedMb: 0,
        totalDataRemainingMb: 0,
        networks: {}
      },
      comparisonData: {
        previousTotal: 0,
        previousRemaining: 0,
        currentTotal: 0,
        currentRemaining: 0,
        dataUsed: 0,
        percentageUsed: 0
      },
      searchQuery: '',
      filterStatus: '',
      filterAlertLevel: '',
      sortBy: 'msisdn',
      loading: false,
      apiNotification: null,
      API_KEY: "sEYm9fsvSzYhYGXz0X59UmWCM1epshWe2ZctsFhCU3W-2KRlP2s0uW5FCS1LbKxQy7ROFJudawRWAobF9XwPbw",
      BASE_URL: "https://app.simcontrol.co.za/api",
      USE_MOCK_DATA: true,
      THRESHOLDS: {
        CRITICAL_DATA_MIN: 0,
        CRITICAL_DATA_MAX: 10,
        LOW_DATA_MIN: 10,
        LOW_DATA_MAX: 50,
        MEDIUM_DATA_MIN: 50,
        MEDIUM_DATA_MAX: 100,
        GOOD_DATA_MIN: 100
      }
    };
  },
  computed: {
    filteredSims() {
      let filtered = this.sims;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(function (sim) {
          return sim.msisdn.toLowerCase().includes(query) ||
            sim.network.toLowerCase().includes(query) ||
            sim.status.toLowerCase().includes(query);
        });
      }

      if (this.filterStatus) {
        const status = this.filterStatus;
        filtered = filtered.filter(function (sim) {
          return sim.status === status;
        });
      }

      if (this.filterAlertLevel) {
        const alertLevel = this.filterAlertLevel;
        filtered = filtered.filter(function (sim) {
          return sim.alert_level === alertLevel;
        });
      }

      return filtered;
    },
    sortedAndFilteredSims() {
      const sims = this.filteredSims.slice();
      const self = this;

      switch (this.sortBy) {
        case 'data-asc':
          return sims.sort(function (a, b) {
            return self.safeFloat(a.RemainingMb) - self.safeFloat(b.RemainingMb);
          });
        case 'data-desc':
          return sims.sort(function (a, b) {
            return self.safeFloat(b.RemainingMb) - self.safeFloat(a.RemainingMb);
          });
        case 'status':
          return sims.sort(function (a, b) {
            return a.status.localeCompare(b.status);
          });
        case 'msisdn':
        default:
          return sims.sort(function (a, b) {
            return a.msisdn.localeCompare(b.msisdn);
          });
      }
    }
  },
  methods: {
    safeFloat: function (value) {
      var num = parseFloat(value);
      return isNaN(num) ? 0 : num;
    },
    extractSimsFromResponse: function (data) {
      if (Array.isArray(data)) return data;
      if (typeof data === 'object' && data) {
        var keys = ['results', 'data', 'sims', 'items', 'payload'];
        for (var i = 0; i < keys.length; i++) {
          if (Array.isArray(data[keys[i]])) return data[keys[i]];
        }
        if ('id' in data || 'msisdn' in data) return [data];
      }
      return [];
    },
    getAllSims: function () {
      var self = this;
      // if (this.USE_MOCK_DATA) {
      //   console.log('Using JSON dataset from public/sims_report.json');
      //   return new Promise(function (resolve) {
      //     setTimeout(function () {
      //       fetch('/sims_report.json')
      //         .then(function (response) {
      //           if (!response.ok) {
      //             throw new Error('Failed to load sims_report.json: ' + response.status);
      //           }
      //           return response.json();
      //         })
      //         .then(function (data) {
      //           resolve(data);
      //         })
      //         .catch(function (e) {
      //           console.error('Failed to fetch mock data:', e);
      //           resolve([]);
      //         });
      //     }, 800);
      //   });
      // }

      var allSims = [];
      var page = 1;
      var pageSize = 100;
      var HEADERS = {
        // "Authorization": "Token " + this.API_KEY,
        "Content-Type": "application/json",
        "x-api-key": this.API_KEY
      };

      function fetchPage() {
        var url = self.BASE_URL + "/sims?page=" + page + "&page_size=" + pageSize;
        // return fetch(url, { headers: HEADERS })
        //   .then(function (response) {
        //     debugger
        //     if (!response.ok) return null;
        //     return response.json();
        //   })
        //   .then(function (data) {
        //     if (!data) return allSims;
        //     var pageSims = self.extractSimsFromResponse(data);
        //     if (!pageSims || pageSims.length === 0) return allSims;

        //     allSims = allSims.concat(pageSims);
        //     if (pageSims.length < pageSize) return allSims;

        //     page++;
        //     return fetchPage();
        //   })
        //   .catch(function (e) {
        //     console.error('API fetch error:', e);
        //     return allSims;
        //   });
        var HEADERS2 = {
          "Authorization": "Token " + self.API_KEY,
          "Content-Type": "application/json",
          "x-api-key": self.API_KEY
        };
        return axios
          .get(
            url,
            {
              headers: HEADERS2
            }
          )
          .then((data) => {
            debugger
            if (!data) return allSims;
            var pageSims = self.extractSimsFromResponse(data);
            if (!pageSims || pageSims.length === 0) return allSims;

            allSims = allSims.concat(pageSims);
            if (pageSims.length < pageSize) return allSims;

            page++;
            return fetchPage();
          })
          .catch(async (error) => {
            console.error('API fetch error:', error);
            return allSims;
          })
          // .finally(() => {

          // });
      }

      return fetchPage();
    },
    enrichSims: function (sims) {
      var enriched = [];
      for (var i = 0; i < sims.length; i++) {
        var s = sims[i];
        s.RemainingMb = this.safeFloat(s.data_balance_in_mb);
        s.DataUsedMb = this.safeFloat(s.data_bundle_size_mb) - s.RemainingMb;

        var mb = s.RemainingMb;
        if (mb <= this.THRESHOLDS.CRITICAL_DATA_MAX) {
          s.alert_level = 'critical';
        } else if (mb <= this.THRESHOLDS.LOW_DATA_MAX) {
          s.alert_level = 'warning';
        } else {
          s.alert_level = 'ok';
        }

        var netMap = { 10: 'Vodacom', 11: 'MTN' };
        s.network = netMap[s.network_id] || 'Network ' + s.network_id;

        enriched.push(s);
      }
      return enriched;
    },
    analyzeData: function (sims) {
      var total = sims.length;
      var active = 0;
      var inactive = 0;
      var suspended = 0;
      var criticalAlerts = 0;
      var warningAlerts = 0;
      var okCount = 0;
      var criticalData = 0;
      var lowData = 0;
      var mediumData = 0;
      var goodData = 0;
      var totalDataUsedMb = 0;
      var totalDataRemainingMb = 0;
      var networks = {};

      for (var i = 0; i < sims.length; i++) {
        var s = sims[i];
        var status = (s.status || '').toLowerCase();
        if (status === 'active') active++;
        if (status === 'inactive') inactive++;
        if (status === 'suspended') suspended++;

        var alert = (s.alert_level || '').toLowerCase();
        if (alert === 'critical') criticalAlerts++;
        if (alert === 'warning') warningAlerts++;
        if (alert === 'ok') okCount++;

        var mb = this.safeFloat(s.RemainingMb);
        if (mb >= this.THRESHOLDS.CRITICAL_DATA_MIN && mb <= this.THRESHOLDS.CRITICAL_DATA_MAX) {
          criticalData++;
        } else if (mb > this.THRESHOLDS.CRITICAL_DATA_MAX && mb <= this.THRESHOLDS.LOW_DATA_MAX) {
          lowData++;
        } else if (mb > this.THRESHOLDS.LOW_DATA_MAX && mb <= this.THRESHOLDS.MEDIUM_DATA_MAX) {
          mediumData++;
        } else if (mb > this.THRESHOLDS.MEDIUM_DATA_MAX) {
          goodData++;
        }

        totalDataUsedMb += this.safeFloat(s.DataUsedMb);
        totalDataRemainingMb += this.safeFloat(s.RemainingMb);

        var n = (s.network || 'unknown').toLowerCase();
        networks[n] = (networks[n] || 0) + 1;
      }

      return {
        total: total,
        active: active,
        inactive: inactive,
        suspended: suspended,
        criticalAlerts: criticalAlerts,
        warningAlerts: warningAlerts,
        okCount: okCount,
        criticalData: criticalData,
        lowData: lowData,
        mediumData: mediumData,
        goodData: goodData,
        totalDataUsedMb: Math.round(totalDataUsedMb * 10) / 10,
        totalDataRemainingMb: Math.round(totalDataRemainingMb * 10) / 10,
        networks: networks
      };
    },
    calculateComparison: function () {
      var currentTotal = this.stats.totalDataUsedMb + this.stats.totalDataRemainingMb;
      var currentRemaining = this.stats.totalDataRemainingMb;

      var previousTotal = currentTotal * 1.2;
      var previousRemaining = currentRemaining + (currentTotal * 0.2);
      var dataUsed = previousRemaining - currentRemaining;
      var percentageUsed = (dataUsed / previousTotal) * 100;

      this.comparisonData = {
        previousTotal: Math.round(previousTotal * 10) / 10,
        previousRemaining: Math.round(previousRemaining * 10) / 10,
        currentTotal: Math.round(currentTotal * 10) / 10,
        currentRemaining: Math.round(currentRemaining * 10) / 10,
        dataUsed: Math.round(dataUsed * 10) / 10,
        percentageUsed: Math.round(percentageUsed * 10) / 10
      };
    },
    loadData: function () {
      var self = this;
      this.loading = true;

      this.getAllSims()
        .then(function (rawSims) {
          if (rawSims.length === 0) {
            if (!self.USE_MOCK_DATA) {
              self.USE_MOCK_DATA = true;
              return self.getAllSims().then(function (retryRawSims) {
                if (retryRawSims.length === 0) {
                  throw new Error('No data available from both API and mock data');
                }
                return retryRawSims;
              });
            } else {
              throw new Error('No data available');
            }
          }
          return rawSims;
        })
        .then(function (rawSims) {
          self.sims = self.enrichSims(rawSims);
          self.stats = self.analyzeData(self.sims);
          self.calculateComparison();

          self.$nextTick(function () {
            self.initCharts();
          });
        })
        .catch(function (error) {
          console.error('Error loading data:', error);
          self.sims = [];
          self.stats = {
            total: 0, active: 0, inactive: 0, suspended: 0,
            criticalAlerts: 0, warningAlerts: 0, okCount: 0,
            criticalData: 0, lowData: 0, mediumData: 0, goodData: 0,
            totalDataUsedMb: 0, totalDataRemainingMb: 0, networks: {}
          };
        })
        .finally(function () {
          self.loading = false;
        });
    },
    refreshData: function () {
      var self = this;
      this.apiNotification = null;
      var wasUsingMock = this.USE_MOCK_DATA;
      this.USE_MOCK_DATA = false;

      this.getAllSims()
        .then(function (rawSims) {
          if (rawSims.length === 0) {
            self.apiNotification = {
              type: 'error',
              message: 'Failed to load data. Please check your connection and try again.'
            };
            self.USE_MOCK_DATA = true;
            return self.getAllSims();
          }
          return rawSims;
        })
        .then(function (rawSims) {
          if (rawSims.length > 0) {
            self.sims = self.enrichSims(rawSims);
            self.stats = self.analyzeData(self.sims);
            self.calculateComparison();

            self.$nextTick(function () {
              self.initCharts();
            });

            if (self.USE_MOCK_DATA && !wasUsingMock) {
              self.apiNotification = {
                type: 'warning',
                message: 'Live API unavailable. Using sample data. Check your internet connection or API credentials.'
              };
            } else if (!self.USE_MOCK_DATA) {
              self.apiNotification = {
                type: 'success',
                message: 'Successfully loaded data from SimControl API'
              };
            }
          }
        })
        .catch(function (error) {
          console.error('Refresh error:', error);
          self.apiNotification = {
            type: 'error',
            message: 'Failed to refresh data: ' + error.message
          };
        })
        .finally(function () {
          self.loading = false;
          setTimeout(function () {
            if (self.apiNotification) {
              self.apiNotification = null;
            }
          }, 6000);
        });
    },
    goToSimControl: function (msisdn) {
      var simControlUrl = 'https://app.simcontrol.co.za/dashboard/sims/' + msisdn;
      window.open(simControlUrl, '_blank');
    },
    initCharts: function () {
      this.createDataDistributionChart();
      this.createStatusChart();
    },
    createDataDistributionChart: function () {
      var canvas = this.$refs.dataDistributionChart;
      if (!canvas) return;

      var ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      var data = [
        this.stats.criticalData,
        this.stats.lowData,
        this.stats.mediumData,
        this.stats.goodData
      ];
      var total = data.reduce(function (a, b) { return a + b; }, 0);
      if (total === 0) return;

      var colors = ['#22c55e', '#eab308', '#64748b'];

      var currentAngle = 0;
      var centerX = canvas.width / 2;
      var centerY = canvas.height / 2;
      var radius = Math.min(centerX, centerY) - 10;

      for (var i = 0; i < data.length; i++) {
        var value = data[i];
        var sliceAngle = (value / total) * 2 * Math.PI;

        ctx.fillStyle = colors[i];
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
        ctx.closePath();
        ctx.fill();

        currentAngle += sliceAngle;
      }

      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.6, 0, 2 * Math.PI);
      ctx.fill();
    }
  },
  mounted: function () {
    this.loadData();
  }
};
</script>

<style>
:root {
  --autumn-primary: #D2691E;
  --autumn-secondary: #CD853F;
  --autumn-accent: #FF8C00;
  --autumn-warm: #FFA500;
  --autumn-deep: #8B4513;
  --autumn-light: #FFE4B5;

  --fams-primary: #2C3E50;
  --fams-secondary: #3498DB;
  --fams-success: #27AE60;
  --fams-warning: #F39C12;
  --fams-danger: #E74C3C;
  --fams-dark: #34495E;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #e2e8f0;
  height: 100vh;
  overflow: hidden;
  width: 100%;
}

#app {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  max-width: none;
}

/* Header */
.header-toolbar {
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  padding: 16px 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: var(--autumn-primary);
}

.icon-pulse {
  font-size: 1.5rem;
  animation: pulse 2s infinite;
}

.dashboard-title {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.live-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-dot.loading {
  background: #f39c12;
  animation: pulse-loading 1s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

@keyframes pulse-loading {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }
}

/* API Notification */
.api-notification {
  padding: 12px 20px;
  border-bottom: 1px solid;
}

.api-notification.notification-success {
  background: #dcfce7;
  border-color: #86efac;
}

.api-notification.notification-warning {
  background: #fef3c7;
  border-color: #fde047;
}

.api-notification.notification-error {
  background: #fee2e2;
  border-color: #fca5a5;
}

.notification-content {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notification-message {
  font-size: 0.875rem;
  font-weight: 500;
}

.notification-success .notification-message {
  color: #14532d;
}

.notification-warning .notification-message {
  color: #78350f;
}

.notification-error .notification-message {
  color: #7f1d1d;
}

.notification-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.notification-close:hover {
  opacity: 1;
}

/* Main Content */
.main-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
  width: 100%;
  background: #e2e8f0;
  position: relative;
}

/* Statistics Section */
.stats-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.stats-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
}

.stats-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.stats-wrapper {
  padding: 16px 20px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border-left: 4px solid #D2691E;
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  background: #ffffff;
}

.stat-label {
  color: #2c3e50;
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 600;
}

.stat-value {
  font-size: 1.75rem;
  color: #2c3e50;
  font-weight: bold;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.stat-change {
  font-size: 0.75rem;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-change.positive {
  color: var(--fams-success);
}

.stat-change.negative {
  color: var(--fams-danger);
}

.stat-icon {
  font-size: 0.875rem;
}

/* Comparison Section */
.comparison-content {
  padding: 20px;
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.comparison-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.comparison-card.highlight {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe4b5 100%);
  border-color: #D2691E;
}

.comparison-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.comparison-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #34495E;
  margin-bottom: 12px;
}

.comparison-detail {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.detail-label {
  font-size: 0.75rem;
  color: #64748b;
}

.detail-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.detail-value.primary {
  color: #3498DB;
}

.detail-value.accent {
  color: #D2691E;
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.chart-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #cbd5e1;
  overflow: hidden;
}

.chart-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.chart-body {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.chart-legend {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #64748b;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.legend-dot.critical {
  background: #ef4444;
}

.legend-dot.warning {
  background: #f97316;
}

.legend-dot.medium {
  background: #eab308;
}

.legend-dot.good {
  background: #22c55e;
}

.legend-dot.active {
  background: #22c55e;
}

.legend-dot.suspended {
  background: #eab308;
}

.legend-dot.inactive {
  background: #64748b;
}

/* Search Section */
.search-section {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: visible;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.filter-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  border-radius: 12px 12px 0 0;
}

.filter-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
  margin: 0;
}

.search-controls-wrapper {
  padding: 16px 20px;
}

.search-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.search-group {
  display: flex;
  flex-direction: column;
}

.search-label {
  font-size: 0.875rem;
  color: #34495E;
  margin-bottom: 4px;
  font-weight: 500;
}

.search-input,
.search-select {
  padding: 10px 12px;
  border: 1px solid #94a3b8;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.search-input:focus,
.search-select:focus {
  outline: none;
  border-color: #D2691E;
  box-shadow: 0 0 0 3px rgba(210, 105, 30, 0.15);
  background: #ffffff;
}

.filter-actions {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.apply-filters-btn {
  padding: 10px 24px;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.apply-filters-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(210, 105, 30, 0.3);
}

/* Grid Container */
.grid-container {
  background: #ffffff;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  width: 100%;
  border: 1px solid #cbd5e1;
}

.grid-header {
  padding: 16px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #cbd5e1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.grid-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #34495E;
}

.grid-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.grid-actions {
  display: flex;
  gap: 8px;
}

.action-button {
  padding: 8px 14px;
  background: white;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  color: #475569;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.action-button:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #D2691E;
  color: #D2691E;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 1rem;
}

/* Table */
.table-container {
  overflow: auto;
  position: relative;
  max-height: 500px;
  width: 100%;
  background: #ffffff;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(241, 245, 249, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loader {
  width: 48px;
  height: 48px;
  border: 4px solid var(--autumn-light);
  border-top: 4px solid var(--autumn-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
  z-index: 10;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.data-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: #ffffff;
}

.data-table tbody tr:hover {
  background: #f1f5f9;
  cursor: pointer;
}

.data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.data-table tbody tr:nth-child(even):hover {
  background: #f1f5f9;
}

.data-table td {
  padding: 12px 16px;
  font-size: 0.875rem;
  color: var(--fams-dark);
}

.device-id {
  font-weight: 600;
  color: var(--fams-dark);
}

.activity-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.badge-active {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.badge-suspended {
  background: linear-gradient(135deg, #F39C12, #E67E22);
  color: white;
}

.badge-inactive {
  background: linear-gradient(135deg, #95A5A6, #7F8C8D);
  color: white;
}

.badge-critical {
  background: linear-gradient(135deg, #E74C3C, #C0392B);
  color: white;
}

.badge-warning {
  background: linear-gradient(135deg, #F39C12, #E67E22);
  color: white;
}

.badge-ok {
  background: linear-gradient(135deg, #27AE60, #229954);
  color: white;
}

.data-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar-container {
  width: 80px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progress-bar.critical {
  background: #ef4444;
}

.progress-bar.warning {
  background: #f97316;
}

.progress-bar.medium {
  background: #eab308;
}

.progress-bar.good {
  background: #22c55e;
}

.progress-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.volume-display {
  font-weight: 600;
  color: var(--autumn-primary);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--fams-dark);
  background: #f8fafc;
  border-radius: 8px;
  margin: 20px;
  border: 1px solid #e2e8f0;
}

.empty-icon {
  font-size: 4rem;
  color: var(--autumn-light);
  margin-bottom: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.empty-description {
  color: #6c757d;
  text-align: center;
}

/* Pagination */
.pagination-container {
  padding: 16px 20px;
  border-top: 1px solid #cbd5e1;
  background: #f1f5f9;
}

.pagination-info {
  color: #6c757d;
  font-size: 0.875rem;
}

.per-page-info {
  color: #D2691E;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    padding: 12px;
  }

  .stats-container {
    grid-template-columns: 1fr;
  }

  .search-controls {
    grid-template-columns: 1fr;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }

  .charts-section {
    grid-template-columns: 1fr;
  }
}
</style>

// var colors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];

// var currentAngle = 0;
// var centerX = canvas.width / 2;
// var centerY = canvas.height / 2;
// var radius = Math.min(centerX, centerY) - 10;

// for (var i = 0; i < data.length; i++) { var value=data[i]; var sliceAngle=(value / total) * 2 * Math.PI;
//   ctx.fillStyle=colors[i]; ctx.beginPath(); ctx.moveTo(centerX, centerY); ctx.arc(centerX, centerY, radius,
//   currentAngle, currentAngle + sliceAngle); ctx.closePath(); ctx.fill(); currentAngle +=sliceAngle; }
//   ctx.fillStyle='#f8fafc' ; ctx.beginPath(); ctx.arc(centerX, centerY, radius * 0.6, 0, 2 * Math.PI); ctx.fill(); },
//   createStatusChart: function () { var canvas=this.$refs.statusChart; if (!canvas) return; var
//   ctx=canvas.getContext('2d'); ctx.clearRect(0, 0, canvas.width, canvas.height); var data=[ this.stats.active,
//   this.stats.suspended, this.stats.inactive ]; var total=data.reduce(function (a, b) { return a + b; }, 0); if
//   (total===0) return;
